import type { FormResult } from '../types';
import type { Answer } from '../utils/churn-answers';
import type { SurveyModel } from '../api/survey-model';
import { createAnswerFromQuestionIndex } from '../utils/churn-answers';
import { submitChurnSurvey } from '../api/submit-churn-survey';
import { QUESTIONS_INDEXES } from './useChurnSurveyQuestions';

export function useSubmitChurnSurvey({
  implicitAnswers,
  formConfigData,
}: {
  implicitAnswers?: Record<string, Answer>;
  formConfigData?: SurveyModel;
}): { submitSurvey: (data: FormResult) => Promise<void> } {
  async function submitSurvey(formData: FormResult): Promise<void> {
    if (formConfigData === undefined) {
      return;
    }

    const reasonQuestionData =
      formConfigData.survey.content.questions[QUESTIONS_INDEXES.DOWNGRADE_REASON];

    if (reasonQuestionData.answers === undefined) {
      throw new Error(
        'could not find the answers for the downgradeReason. This should never happen at this point',
      );
    }

    const reasonAnswerIndex = reasonQuestionData.answers.findIndex(
      (answer) => answer.text === formData.downgradeReason,
    );

    if (reasonAnswerIndex === -1) {
      throw new Error(
        'could not find the downgradeReason in the form. This should never happen at this point',
      );
    }

    if (reasonQuestionData.nextByAnswer === undefined) {
      throw new Error(
        'reasonQuestionData.nextByAnswer is undefined. This should never happen at this point',
      );
    }

    const explanationsForReasonUuid = reasonQuestionData.nextByAnswer[reasonAnswerIndex].replace(
      'question:',
      '',
    );
    const explanationQuestionData = formConfigData.survey.content.questions.find(
      (question) => question.uuid === explanationsForReasonUuid,
    );

    if (explanationQuestionData === undefined) {
      throw new Error(
        'explanationQuestionData is undefined. This should never happen at this point',
      );
    }

    const explanationsAnswers = formData.downgradeExplanations.reduce<Answer[]>(
      (result, explanation): Answer[] => {
        if (explanation.option.length > 0) {
          result.push({
            question: explanationQuestionData.text,
            answer: explanation.option,
            questionUuid: explanationQuestionData.uuid.substring(0, 8),
            comment: explanation.comment ?? null,
          });
        }
        return result;
      },
      [],
    );

    if (implicitAnswers) {
      const finalAnswers = [
        createAnswerFromQuestionIndex(
          QUESTIONS_INDEXES.DOWNGRADE_REASON,
          formData.downgradeReason,
          formConfigData,
        ),
        ...explanationsAnswers,
        createAnswerFromQuestionIndex(
          QUESTIONS_INDEXES.MORE_DETAILS,
          formData.moreDetails,
          formConfigData,
        ),
        ...Object.values(implicitAnswers),
      ];

      await submitChurnSurvey(
        String(formConfigData.survey.id),
        finalAnswers,
        window.location.pathname,
      );
    }
  }

  return { submitSurvey };
}
